if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Nordic Spa: if add-ons are ticked, POST a multi-line items[] payload as
        // JSON (main product + each add-on variant) instead of the single-item form.
        // Scope the lookup to THIS form's own product-info and skip quick-add modals
        // (which belong to complementary products) — an unscoped, page-global lookup
        // would attach the main product's add-ons to any product form on the page.
        const addonsElements = this.closest('quick-add-modal')
          ? []
          : Array.from((this.closest('product-info') || document).querySelectorAll('product-addons'));
        const addonItems = addonsElements.flatMap((el) => el.selectedItems);

        // Nordic Spa: the lines this submit asks for, kept for the add_to_cart event. See
        // reportAddToCart for why the quantities cannot come from the response.
        let requestedLines = [{ id: formData.get('id'), quantity: parseInt(formData.get('quantity')) || 1 }];

        if (addonItems.length > 0) {
          const body = {
            items: [
              { id: formData.get('id'), quantity: parseInt(formData.get('quantity')) || 1 },
              ...addonItems,
            ],
          };
          requestedLines = body.items;
          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(body);
          this.addonsElements = addonsElements;
        } else {
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        // Nordic Spa: set once a successful add is about to leave for /cart. See .finally.
        let leavingForCart = false;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              leavingForCart = true;
              this.reportAddToCart(requestedLines, response);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);
            this.reportAddToCart(requestedLines, response);

            // Nordic Spa: a multi-item items[] add returns { items, sections } with no
            // top-level `key`. cart-notification/cart-drawer key their rendered product
            // section on response.key, so without it renderContents throws (null section)
            // and the notification never opens. Fall back to the main product's line-item
            // key so the notification features the hot tub and the count/drawer update.
            if (!response.key && response.items && response.items.length) {
              response.key = response.items[0].key;
            }

            // Nordic Spa: uncheck consumed add-ons after a successful multi-item add.
            this.addonsElements?.forEach((el) => el.clear());

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            const rearm = () => {
              this.submitButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner').classList.add('hidden');
            };
            // Nordic Spa: while the page is leaving for /cart the button stays busy. The next page
            // takes a moment to arrive, and a button re-armed in that window takes a second click
            // and adds the order twice. If the browser restores this page from the back-forward
            // cache, re-arm it then.
            if (leavingForCart) {
              window.addEventListener('pageshow', (e) => e.persisted && rearm(), { once: true });
            } else {
              rearm();
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      // Nordic Spa: add_to_cart for GA4, through the GTM container the theme loads
      // (snippets/google-tag-manager.liquid). It used to come from the Shopify custom pixel.
      //
      // Pushed from here and not from a PUB_SUB_EVENTS.cartUpdate subscriber. This store runs
      // cart_type "page", so this form has no cart drawer or notification and goes straight to
      // /cart WITHOUT publishing cartUpdate. A subscriber would have looked right and reported nil.
      //
      // Quantities come from the REQUEST. /cart/add.js returns each line's TOTAL quantity in the
      // cart after the add, not the amount just added, so a second add of the same item would
      // report two. The response only supplies the SKU, names and unit price, matched on variant.
      //
      // The move to /cart does not cut the event off, so nothing waits for it: GTM handles the push
      // synchronously and the Google tag sends its queue as the page unloads (measured on dev).
      reportAddToCart(requested, response) {
        try {
          const analytics = window.nordicAnalytics;
          if (!analytics) return;
          const lines = (response && response.items) || [response];
          const items = requested.map((request) => {
            const line = lines.find((l) => l && String(l.variant_id) === String(request.id));
            return analytics.item(line, parseInt(request.quantity) || 1, request.id);
          });
          const value = items.reduce((sum, item) => sum + (item.price || 0) * item.quantity, 0);
          analytics.push('add_to_cart', { ecommerce: { currency: analytics.currency, value, items } });
        } catch (e) {
          // Analytics must never block the add.
        }
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
